export const CLOUD_ENV_KEYS = [
  'VITE_API_BASE_URL',
  'VITE_COGNITO_REGION',
  'VITE_COGNITO_USER_POOL_ID',
  'VITE_COGNITO_APP_CLIENT_ID',
] as const;

type CloudEnvKey = (typeof CLOUD_ENV_KEYS)[number];

type CloudEnv = Readonly<Partial<Record<CloudEnvKey, string>>>;

export type CloudConfig = Readonly<{
  apiBaseUrl: string;
  appClientId: string;
  callbackUrl: string;
  cognitoIssuer: string;
  logoutUrl: string;
  region: string;
  userPoolId: string;
}>;

export type CloudConfigResult =
  | Readonly<{ status: 'disabled' }>
  | Readonly<{
      status: 'invalid';
      message: string;
      missingKeys: readonly CloudEnvKey[];
    }>
  | Readonly<{ status: 'ready'; config: CloudConfig }>;

const REGION_PATTERN = /^[a-z]{2}(?:-gov)?-[a-z]+-\d$/;
const USER_POOL_PATTERN = /^[a-z]{2}(?:-gov)?-[a-z]+-\d_[A-Za-z0-9]+$/;

export function readCloudConfig(
  env: CloudEnv = currentCloudEnv(),
  origin: string = window.location.origin,
  pathname: string = window.location.pathname,
): CloudConfigResult {
  const values: Record<CloudEnvKey, string> = {
    VITE_API_BASE_URL: env.VITE_API_BASE_URL?.trim() ?? '',
    VITE_COGNITO_APP_CLIENT_ID: env.VITE_COGNITO_APP_CLIENT_ID?.trim() ?? '',
    VITE_COGNITO_REGION: env.VITE_COGNITO_REGION?.trim() ?? '',
    VITE_COGNITO_USER_POOL_ID: env.VITE_COGNITO_USER_POOL_ID?.trim() ?? '',
  };
  const configuredKeys = CLOUD_ENV_KEYS.filter(key => values[key] !== '');

  if (configuredKeys.length === 0) {
    return { status: 'disabled' };
  }

  const missingKeys = CLOUD_ENV_KEYS.filter(key => values[key] === '');
  if (missingKeys.length > 0) {
    return {
      status: 'invalid',
      message: 'Cloud configuration is incomplete.',
      missingKeys,
    };
  }

  const apiBaseUrl = parseUrl(values.VITE_API_BASE_URL);
  const appBasePath = resolveAppBasePath(pathname);
  const callbackUrl = parseUrl(`${appBasePath}/cloud/callback`, origin);
  const logoutUrl = parseUrl(`${appBasePath}/cloud`, origin);
  const region = values.VITE_COGNITO_REGION;
  const userPoolId = values.VITE_COGNITO_USER_POOL_ID;

  if (
    apiBaseUrl === null ||
    callbackUrl === null ||
    logoutUrl === null ||
    !isAllowedProtocol(apiBaseUrl) ||
    !isAllowedProtocol(callbackUrl) ||
    !REGION_PATTERN.test(region) ||
    !USER_POOL_PATTERN.test(userPoolId) ||
    !userPoolId.startsWith(`${region}_`)
  ) {
    return {
      status: 'invalid',
      message: 'Cloud configuration contains an invalid URL or Cognito ID.',
      missingKeys: [],
    };
  }

  return {
    status: 'ready',
    config: {
      apiBaseUrl: withoutTrailingSlash(apiBaseUrl.href),
      appClientId: values.VITE_COGNITO_APP_CLIENT_ID,
      callbackUrl: callbackUrl.href,
      cognitoIssuer: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
      logoutUrl: logoutUrl.href,
      region,
      userPoolId,
    },
  };
}

function resolveAppBasePath(pathname: string): string {
  const normalizedPathname = `/${pathname}`
    .replace(/\/{2,}/g, '/')
    .replace(/\/$/, '');

  for (const route of ['/cloud/callback', '/cloud', '/entry']) {
    if (normalizedPathname === route) return '';
    if (normalizedPathname.endsWith(route)) {
      return normalizedPathname.slice(0, -route.length);
    }
  }

  return normalizedPathname === '' ? '' : normalizedPathname;
}

function currentCloudEnv(): CloudEnv {
  return {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_COGNITO_APP_CLIENT_ID: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
    VITE_COGNITO_REGION: import.meta.env.VITE_COGNITO_REGION,
    VITE_COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  };
}

function parseUrl(value: string, base?: string): URL | null {
  try {
    return new URL(value, base);
  } catch {
    return null;
  }
}

function isAllowedProtocol(url: URL): boolean {
  return (
    url.protocol === 'https:' ||
    (url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1'))
  );
}

function withoutTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
